import { formatDate, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  DestroyRef,
  ElementRef,
  inject,
  LOCALE_ID,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, type ParamMap } from '@angular/router';
import {
  catchError,
  debounceTime,
  EMPTY,
  filter,
  finalize,
  fromEvent,
  merge,
  of,
  switchMap,
  take,
  tap,
  timer,
  type Observable,
  type Subscription,
} from 'rxjs';

import { JobAttachmentsService } from '../../core/job-attachments.service';
import { JobChatService } from '../../core/job-chat.service';
import {
  JOB_CHAT_SENDER_STAFF,
  JOB_CHAT_SENDER_SYSTEM,
  JOB_CHAT_SENDER_VENDOR,
  type JobChatMessage,
} from '../../core/job-chat.types';
import { JobDetailsService } from '../../core/job-details.service';
import { VendorPortalLiveService } from '../../core/vendor-portal-live.service';
import {
  JOB_STATUS_COMPLETE_KEY,
  VENDOR_PORTAL_LINE_CHANGE,
  type JobDetailsAction,
  type JobDetailsData,
  type JobDetailsEstimateLineItem,
  type JobDetailsEstimateSummary,
  type JobDetailsGetResult,
  type JobTimelineEntry,
  type VendorPortalEstimateLineEdit,
  type VendorPortalOptionEdit,
} from '../../core/job-details.types';

/** How often to re-poll the chat thread while the Messages accordion is open (ms). No polling
 * happens while it's collapsed — job details already refresh on tab focus / cross-tab signal. */
const CHAT_POLL_INTERVAL_MS = 15000;

export type HeaderVersion = 'logo' | 'label';

/** New-line item-type picker option — mirrors the labor/trip labels from the admin portal's
 * CUSTOM_CHARGE_TYPE_OPTIONS (rfi-admin-portal-v2/src/app/models/on-site-estimate.model.ts),
 * minus the tech-count variants (this form already has its own separate Techs number input).
 * Selecting a type sets the name the line is saved under (NEW_LINE_SAVED_ITEM_NAMES), matching how this endpoint has
 * always distinguished line types (VendorEstimateService.IsTripChargeItemName matches on the
 * "material" substring) — no new backend field or charge-type-key resolution involved. */
/** Job type key the API treats as "Emergency" when choosing emergency labor/trip/helper rates
 * (RFIJobOps VendorRateLookupService.EmergencyJobTypeKey). */
const EMERGENCY_JOB_TYPE_KEY = 'fc078fd5-5ddc-4088-8a9f-d982436e20fd';

export interface NewLineItemTypeOption {
  key: string;
  label: string;
}

/** The item name a line added here is SAVED under, by the type picked (the dropdown shows the short label). */
export const NEW_LINE_SAVED_ITEM_NAMES: Record<string, string> = {
  LABOR: 'Standard Hourly Rate',
  EMERGENCY_LABOR: 'Overtime/ Weekend Hourly Rate',
  HELPER: 'Helper Standard Hourly Rate',
  EMERGENCY_HELPER: 'Helper Overtime/ Weekend Hourly Rate',
  MATERIAL: 'MATERIALS',
  TRIP: 'Flat Rate Trip Charge',
  EMERGENCY_TRIP: 'Overtime/ Weekend Flat Rate Trip Charge',
};

export const NEW_LABOR_ITEM_TYPE_OPTIONS: NewLineItemTypeOption[] = [
  { key: 'LABOR', label: 'Labor' },
  { key: 'EMERGENCY_LABOR', label: 'Emergency Labor' },
];

/** "Emergency Helper" is the Helper Overtime/ Weekend Hourly Rate, with its own (emergency helper) rate. */
export const NEW_HELPER_ITEM_TYPE_OPTIONS: NewLineItemTypeOption[] = [
  { key: 'HELPER', label: 'Helper' },
  { key: 'EMERGENCY_HELPER', label: 'Emergency Helper' },
];

export const NEW_TRIP_ITEM_TYPE_OPTIONS: NewLineItemTypeOption[] = [
  { key: 'TRIP', label: 'Trip' },
  { key: 'EMERGENCY_TRIP', label: 'Emergency Trip' },
];

/** One trip charge per estimate. A trip charge shows up as "Flat Rate Trip Charge" or "Overtime/Weekend Flat Rate Trip Charge" (and, for a
 * line added here, "Trip" / "Emergency Trip"): a non-labor line with "trip" in its name, or any line added with "+ Add Trip Line". */
export function isTripChargeDraft(line: Pick<EditLineDraft, 'isLabor' | 'isNewTrip' | 'itemName'>): boolean {
  if (line.isLabor) {
    return false;
  }
  return line.isNewTrip || /trip/i.test(line.itemName ?? '');
}

/**
 * What the vendor sees as a line's name: the labor rate name reads just "Labor" (Standard / Overtime-Weekend Hourly Rate) and the helper
 * one just "Helper" (Helper Standard / Helper Overtime-Weekend Hourly Rate, Emergency Helper). Whatever follows the rate name is kept,
 * so "Standard Hourly Rate - 5.00 hours X 5 Tech on Site" reads "Labor - 5.00 hours X 5 Tech on Site". Display only: the stored item
 * name is never changed, because it tells RFI which rate the line is. A labor name that is none of these stays as it is.
 */
export function laborDisplayName(itemName: string | null | undefined, isLabor: boolean | null | undefined): string {
  const name = itemName ?? '';
  if (!isLabor) {
    return name;
  }
  const rate = String.raw`(?:(?:standard|emergency|overtime\s*\/?\s*weekend)\s+)?hourly\s+rate`;
  return name
    .replace(new RegExp(String.raw`^\s*helper\s+${rate}`, 'i'), 'Helper')
    .replace(/^\s*emergency\s+helper\b/i, 'Helper')
    .replace(new RegExp(String.raw`^\s*${rate}`, 'i'), 'Labor');
}

/** In-progress line state for the vendor portal's in-line estimate edit form. */
export interface EditLineDraft {
  /** Stable per-line id for the UI's for-loop tracking — a real lineItemKey for existing lines, a generated placeholder for new ones. */
  draftId: string;
  /** Real backend key; empty for a line that doesn't exist in the DB yet. */
  lineItemKey: string;
  isNew: boolean;
  markedDeleted: boolean;
  isLabor: boolean;
  itemName: string;
  description: string;
  /** RFI's note for this line (read-only, shown while the vendor edits); empty when there is none. */
  adminMessage?: string;
  qty: string;
  rate: string;
  /** Labor only. */
  techOnSite: string;
  /** True when rate is fixed to the vendor's profile rate — input disabled, value forced. */
  isRateLocked: boolean;
  /** True only for trip-charge lines with a locked rate — qty also fixed. */
  isQuantityLocked: boolean;
  /** New Labor/Trip lines only — which NEW_LABOR_ITEM_TYPE_OPTIONS/NEW_TRIP_ITEM_TYPE_OPTIONS
   * entry is selected, so the dropdown can show the right option. Display-only: changing this
   * just updates itemName text (see onNewLineTypeChange). Empty for existing lines and for new
   * Material lines (no picker — always "Material"). */
  newItemTypeKey: string;
  /** True for a new (isNew) non-labor line added via "+ Add Trip Line" — distinguishes it from
   * a new Material line, both of which share isLabor=false. Existing lines don't need this;
   * IsTripChargeItemName's name-based check already covers them. */
  isNewTrip: boolean;
  /** True for a new (isNew) labor line added via "+ Add Helper Line". It is a labor line (hours x
   * techs x rate) named "Helper"/"Emergency Helper" by the job type, with no type picker. */
  isNewHelper: boolean;
  /** The estimate (option) this line belongs to — new lines are added to, and saved on, that option only. */
  estimateKey: string;
  /** While RFI's counter is open: the most this line may go back up to (labor: total hours = hours x techs). Null = no limit. */
  maxQty: number | null;
  maxRate: number | null;
  /** The line's total before RFI's latest change: shown struck out beside the new total. Null for a line RFI did not change and for new lines. */
  previousTotal: number | null;
}

export const WORK_ORDER_PORTAL_CROSS_TAB_REFRESH_KEY = 'rfi-vendor-portal:job-details-refresh';

export interface WorkOrderPortalCrossTabPayload {
  jobKey: string;
  vendorKey: string;
  updatedAt?: number;
}

function normalizeStatus(s: string): string {
  return s.trim().toLowerCase();
}

function normalizeGuid(g: string): string {
  return g.trim().toLowerCase().replace(/[{}]/g, '');
}

function jobStatusKeyIsComplete(key: string | null | undefined): boolean {
  if (key == null || typeof key !== 'string' || !key.trim()) {
    return false;
  }
  return normalizeGuid(key) === normalizeGuid(JOB_STATUS_COMPLETE_KEY);
}

function timelineHasCompletedCompleteStep(data: JobDetailsData): boolean {
  for (const step of data.timeline ?? []) {
    if (!step.time) {
      continue;
    }
    if (normalizeStatus(step.status) === 'complete') {
      return true;
    }
    if (step.completedText && normalizeStatus(step.completedText) === 'complete') {
      return true;
    }
  }
  return false;
}

/** Matches legacy location datetimes embedded in API status text (MM/dd/yyyy hh:mm:ss tt). */
const LEGACY_LOCATION_DATETIME_RE =
  /\b(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}\s+(?:AM|PM))\b/i;

/** Parses a legacy US datetime string as wall-clock job-location time (no timezone shift). */
function parseLegacyLocationDateTime(value: string): Date | null {
  const m = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM)$/i);
  if (!m) {
    return null;
  }
  let hour = parseInt(m[4], 10);
  const isPm = m[7].toUpperCase() === 'PM';
  if (isPm && hour !== 12) {
    hour += 12;
  } else if (!isPm && hour === 12) {
    hour = 0;
  }
  const d = new Date(
    parseInt(m[3], 10),
    parseInt(m[1], 10) - 1,
    parseInt(m[2], 10),
    hour,
    parseInt(m[5], 10),
    parseInt(m[6], 10),
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Parses API `etaTimeLocalize` ISO strings as wall-clock job-location time (no UTC conversion). */
function parseWallClockIso(iso: string): Date | null {
  const m = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) {
    return null;
  }
  const d = new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10),
    parseInt(m[4], 10),
    parseInt(m[5], 10),
    parseInt(m[6] ?? '0', 10),
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

function extractLegacyLocationDateTimeFromStatus(status: string): Date | null {
  const match = status.match(LEGACY_LOCATION_DATETIME_RE);
  if (!match?.[1]) {
    return null;
  }
  return parseLegacyLocationDateTime(match[1]);
}

/** Removes embedded legacy location datetimes from API status labels. */
function stripLegacyLocationDateTimeFromStatus(status: string): string {
  return status
    .replace(/\s+for\s+\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}\s+(?:AM|PM)\b/i, '')
    .replace(/\s+to\s+\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}\s+(?:AM|PM)\b/i, '')
    .replace(/\s+\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}\s+(?:AM|PM)\b\s*$/i, '')
    .trim();
}

/** Connector between ETA status label and formatted job-location time in the title line. */
function timelineDateConnector(label: string): 'for' | 'to' {
  return /\bupdated\b/i.test(label) ? 'to' : 'for';
}

/** Parses API UTC ISO strings; if no timezone suffix is present, treats the value as UTC (Z). */
function parseUtcIsoToDate(iso: string | null | undefined): Date | null {
  if (iso == null || typeof iso !== 'string') {
    return null;
  }
  const t = iso.trim();
  if (!t) {
    return null;
  }
  const hasTz = /Z$/i.test(t) || /[+-]\d{2}:?\d{2}$/.test(t);
  const normalized = hasTz ? t : `${t}Z`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function queryLc(q: ParamMap, key: 'jobkey' | 'vendorkey'): string {
  for (const k of q.keys) {
    if (k.toLowerCase() === key) {
      const v = q.get(k)?.trim();
      if (v) return v;
    }
  }
  return '';
}

function queryLcUrl(key: 'jobkey' | 'vendorkey'): string {
  const loc = typeof globalThis !== 'undefined' ? globalThis.location : undefined;
  if (!loc) return '';
  const parse = (s: string): string => {
    const q = s.startsWith('?') ? s.slice(1) : s;
    if (!q) return '';
    for (const [k, v] of new URLSearchParams(q).entries()) {
      if (k.toLowerCase() === key && v.trim()) return v.trim();
    }
    return '';
  };
  const fromHash = loc.hash.indexOf('?');
  return parse(loc.search) || (fromHash >= 0 ? parse(loc.hash.slice(fromHash)) : '');
}

function resolveJobAndVendorKeys(q: ParamMap): { jobKey: string; vendorKey: string } {
  return {
    jobKey: queryLc(q, 'jobkey') || queryLcUrl('jobkey'),
    vendorKey: queryLc(q, 'vendorkey') || queryLcUrl('vendorkey'),
  };
}

function fixMissingColonAfterScheme(raw: string): string {
  return raw.replace(/^(https?)\/\//i, '$1://');
}

function upgradeHttpToHttpsWhenPageIsSecure(raw: string): string {
  if (typeof globalThis === 'undefined') {
    return raw;
  }
  const loc = globalThis.location;
  if (!loc || loc.protocol !== 'https:') {
    return raw;
  }
  try {
    const parsed = new URL(raw, loc.origin);
    if (parsed.protocol === 'http:') {
      parsed.protocol = 'https:';
      return parsed.href;
    }
  } catch {
    return raw;
  }
  return raw;
}

@Component({
  selector: 'app-work-order-portal',
  templateUrl: './work-order-portal.html',
  styleUrl: './work-order-portal.css',
  imports: [NgTemplateOutlet],
})
export class WorkOrderPortal {
  private readonly route = inject(ActivatedRoute);
  private readonly jobDetailsService = inject(JobDetailsService);
  private readonly live = inject(VendorPortalLiveService);
  private readonly jobAttachmentsService = inject(JobAttachmentsService);
  private readonly jobChatService = inject(JobChatService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locale = inject(LOCALE_ID);
  private readonly sanitizer = inject(DomSanitizer);

  private lastJobKey = '';
  protected lastVendorKey = '';

  protected headerVersion = signal<HeaderVersion>('logo');

  protected readonly jobData = signal<JobDetailsData | null>(null);
  protected readonly loading = signal(false);
  protected readonly loadError = signal(false);
  /** API returned `status: false` with a non-empty `message`; show only this text, no job UI. */
  protected readonly loadApiMessage = signal<string | null>(null);
  protected readonly missingParams = signal(false);

  private readonly attachmentFileInput =
    viewChild<ElementRef<HTMLInputElement>>('attachmentFileInput');

  protected readonly attachmentQueue = signal<File[]>([]);
  protected readonly attachmentRemarks = signal('');
  protected readonly attachmentDragActive = signal(false);
  protected readonly attachmentUploading = signal(false);
  protected readonly attachmentFeedback = signal<{
    kind: 'success' | 'error';
    text: string;
  } | null>(null);

  private attachmentDragDepth = 0;

  // ---- Messages accordion (separate feature from Attachments above — not merged) ----
  protected readonly ChatSenderStaff = JOB_CHAT_SENDER_STAFF;
  protected readonly ChatSenderVendor = JOB_CHAT_SENDER_VENDOR;
  protected readonly ChatSenderSystem = JOB_CHAT_SENDER_SYSTEM;

  private readonly chatThreadEl = viewChild<ElementRef<HTMLDivElement>>('chatThreadEl');

  protected readonly chatOpen = signal(false);
  protected readonly chatMessages = signal<JobChatMessage[]>([]);
  protected readonly chatUnreadCount = signal(0);
  protected readonly chatLoading = signal(false);
  protected readonly chatComposerText = signal('');
  protected readonly chatSending = signal(false);
  protected readonly chatFeedback = signal<{ kind: 'error'; text: string } | null>(null);

  /** True once the vendor has explicitly collapsed the accordion, so a later unread refresh
   * doesn't pop it back open until they open it again themselves. */
  private chatManuallyClosed = false;
  /** The Messages panel opens by itself the first time an estimate is in Negotiate (it then sits beside the estimate); after that it is the vendor's to close. */
  private negotiateChatAutoOpened = false;
  private chatLoadedOnce = false;
  private chatPollSub: Subscription | null = null;

  protected readonly timeline = computed(() => {
    const timelineData = this.jobData()?.timeline ?? [];
    return [...timelineData].reverse();
  });

  /** Once the estimate is back with RFI (pending review, e.g. right after a resubmit) the vendor cannot edit it,
   * whatever the API's canEdit says; every edit control reads canEdit from here. */
  protected readonly estimates = computed(() =>
    (this.jobData()?.estimates ?? []).map((e) =>
      e.stateCode === 'pending_review' && e.canEdit ? { ...e, canEdit: false, vendorCanAddLines: false } : e,
    ),
  );

  protected readonly hasEstimates = computed(() => this.estimates().length > 0);

  /**
   * The job's action buttons. "Edit & Resubmit Estimate" is only for an estimate RFI sent back with Reject for
   * Resubmission (state revision_requested): while RFI has countered (Negotiate), the vendor answers with Accept and
   * Agree or the estimate's own Edit Estimate, and in every other state there is nothing to resubmit. Each action keeps its
   * position in the API's list, so hiding the first button does not turn the next one into the primary (blue) one.
   */
  protected readonly visibleActions = computed(() => {
    const actions = this.jobData()?.actions ?? [];
    const mayResubmit = this.estimates().some((e) => e.stateCode === 'revision_requested');
    return actions
      .map((action, index) => ({ action, index }))
      .filter(({ action }) => mayResubmit || !/resubmit/i.test(action.text ?? ''));
  });

  /** estimateKey currently open for in-line edit, if any. Only one card edits at a time. */
  protected readonly editingEstimateKeys = signal<string[]>([]);
  /** Set while a multiple-option estimate is edited as one batch (every editable option of the group at once). */
  protected readonly editingGroupKey = signal<string | null>(null);
  /** The single estimate being edited on its own (null while a group is being edited or nothing is). */
  protected readonly editingEstimateKey = computed(() =>
    this.editingGroupKey() ? null : (this.editingEstimateKeys()[0] ?? null),
  );

  protected isEditing(estimate: JobDetailsEstimateSummary): boolean {
    return this.editingEstimateKeys().includes(estimate.estimateKey ?? '');
  }

  /** The edit form RFI's Reject for Resubmission opens: a stripped-down look (no job description, no "Your On-site Approval" title, no
   * job action buttons; the status pill moves to the corner and the "changes were requested" message becomes the centre of attention). */
  protected isRevisionEdit(estimate: JobDetailsEstimateSummary): boolean {
    return this.opensStraightIntoEdit(estimate) && this.isEditing(estimate);
  }

  /** RFI has countered this estimate (Negotiate): the same trimmed card as the resubmit form, minus its centred message. */
  protected isNegotiating(estimate: JobDetailsEstimateSummary): boolean {
    return estimate.stateCode === 'negotiate';
  }

  /** Cards that show the status pill in the top right corner and no "Your On-site Approval" title. */
  protected cornerPill(estimate: JobDetailsEstimateSummary): boolean {
    return this.isRevisionEdit(estimate) || this.isNegotiating(estimate);
  }

  /** An estimate on the page is in Negotiate: the job description is hidden then. */
  protected readonly negotiateActive = computed(() => this.estimates().some((e) => this.isNegotiating(e)));

  /** Any estimate on the page is in that form right now. */
  protected readonly revisionEditActive = computed(() => this.estimates().some((e) => this.isRevisionEdit(e)));

  /** Lines of one option while editing. */
  protected draftFor(estimateKey: string | null): EditLineDraft[] {
    return this.editDraft().filter((l) => l.estimateKey === estimateKey);
  }

  /** Live total of one option's non-removed draft lines. */
  protected editDraftTotalFor(estimateKey: string | null): number {
    let total = 0;
    for (const line of this.editDraft()) {
      if (line.estimateKey === estimateKey && !line.markedDeleted) {
        total += this.lineDraftTotal(line);
      }
    }
    return total;
  }

  /** Multiple-option estimates, grouped: one edit/save bar per group (all its options are edited together). */
  protected readonly multiOptionGroups = computed(() => {
    const groups = new Map<string, JobDetailsEstimateSummary[]>();
    for (const estimate of this.estimates()) {
      if (!estimate.isMultiOption) {
        continue;
      }
      const groupKey = estimate.optionGroupKey ?? 'options';
      groups.set(groupKey, [...(groups.get(groupKey) ?? []), estimate]);
    }
    return [...groups.entries()].map(([groupKey, options]) => ({
      groupKey,
      options,
      editable: options.filter((o) => o.canEdit),
    }));
  });

  protected readonly laborItemTypeOptions = NEW_LABOR_ITEM_TYPE_OPTIONS;
  protected readonly tripItemTypeOptions = NEW_TRIP_ITEM_TYPE_OPTIONS;
  protected readonly helperItemTypeOptions = NEW_HELPER_ITEM_TYPE_OPTIONS;

  protected readonly editDraft = signal<EditLineDraft[]>([]);
  protected readonly editSaving = signal(false);
  private refreshInFlight = false;
  private reloadQueued = false;
  protected readonly editError = signal<string | null>(null);
  /** estimateKey of the card that just saved successfully, shown briefly then auto-cleared — the
   * save previously gave no positive confirmation, which made a successful save look like nothing
   * happened and invited the vendor to click "Save Changes" again (observed as 2-3 duplicate saves
   * of the same estimate a few seconds/minutes apart in server logs). */
  protected readonly editSavedEstimateKey = signal<string | null>(null);
  private editSavedTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** Live per-line total while editing — shown next to that line's qty/rate inputs. */
  protected lineDraftTotal(line: EditLineDraft): number {
    const qty = Number(line.qty);
    const rate = Number(line.rate);
    if (Number.isNaN(qty) || Number.isNaN(rate)) {
      return 0;
    }
    return line.isLabor ? qty * rate * (Number(line.techOnSite) || 1) : qty * rate;
  }

  /** Why a draft line must not be saved, or null when it is fine. Single source of truth for the
   * live highlighting, the Save gate and the final payload guard: every non-removed line needs
   * hours/quantity > 0, rate > 0, (labor) a whole number of techs >= 1, and a line total that
   * is still above $0.00 once rounded to cents. Blank, non-numeric, NaN and Infinity all fail. */
  protected lineProblem(line: EditLineDraft): string | null {
    if (line.markedDeleted) {
      return null;
    }
    const positive = (v: string): boolean => {
      const n = Number(v);
      return v.trim() !== '' && Number.isFinite(n) && n > 0;
    };
    if (!positive(line.qty)) {
      return `${line.isLabor ? 'Hours' : 'Quantity'} must be greater than 0.`;
    }
    if (!positive(line.rate)) {
      return 'Rate must be greater than 0.';
    }
    if (line.isLabor) {
      const techs = Number(line.techOnSite);
      if (line.techOnSite.trim() === '' || !Number.isInteger(techs) || techs < 1) {
        return 'Techs must be a whole number of 1 or more.';
      }
    }
    if (Math.round(this.lineDraftTotal(line) * 100) <= 0) {
      return 'Line total must be greater than $0.00.';
    }
    // While RFI's counter is open the vendor may raise or lower any value (30 Oct), but never to 0: the checks above refuse 0.
    return null;
  }

  private trimNumber(value: number): string {
    return String(Math.round(value * 100) / 100);
  }

  /** draftId -> problem, for non-removed lines that currently cannot be saved. */
  protected readonly editLineProblems = computed(() => {
    const problems: Record<string, string> = {};
    for (const line of this.editDraft()) {
      const problem = this.lineProblem(line);
      if (problem) {
        problems[line.draftId] = problem;
      }
    }
    return problems;
  });

  /** Flips on after the first blocked Save so the offending lines stay highlighted (and keep
   * updating live as the vendor fixes them) instead of nagging while a new line is still blank. */
  protected readonly editShowErrors = signal(false);

  /** Live total across all non-deleted draft lines while editing. */
  protected readonly editDraftTotal = computed(() => {
    let total = 0;
    for (const line of this.editDraft()) {
      if (line.markedDeleted) {
        continue;
      }
      total += this.lineDraftTotal(line);
    }
    return total;
  });

  protected readonly approvingEstimateKey = signal<string | null>(null);
  protected readonly approveError = signal<string | null>(null);

  protected canEditEstimate(estimate: JobDetailsEstimateSummary): boolean {
    // Multi-option estimates edit one option (card) at a time, exactly like a single estimate.
    return estimate.canEdit;
  }

  /** Title of one option of a multi-option estimate, so the cards can be told apart. Single-option
   * estimates carry only a default title, so nothing extra is shown for them. */
  protected estimateOptionTitle(estimate: JobDetailsEstimateSummary): string | null {
    const title = estimate.estimateTitle?.trim();
    return estimate.isMultiOption && title ? title : null;
  }

  /** The admin pill's label ("On-site · Pending review"): changes with every admin action. */
  protected estimatePill(estimate: JobDetailsEstimateSummary): string | null {
    const label = estimate.statusLabel?.trim();
    if (!label) {
      return null;
    }
    return estimate.isOnsiteApproval ? `On-site · ${label}` : label;
  }

  /** What the vendor should do / expect for the current state. */
  protected estimateStateMessage(estimate: JobDetailsEstimateSummary): string | null {
    const onsite = estimate.isOnsiteApproval;
    switch (estimate.stateCode) {
      case 'pending_review':
        return onsite
          ? 'Your RFI contact is reviewing your on-site approval. You will be updated here as soon as RFI responds.'
          : 'RFI is reviewing your estimate. You will be updated here as soon as RFI responds.';
      case 'negotiate':
        return "We've suggested updates to your estimate line items. Please review and take the next steps";
      case 'revision_requested':
        return 'Changes were requested. Please edit and resubmit your estimate.';
      case 'approved':
        return onsite
          ? 'Your on-site approval has been approved. You may proceed with the work.'
          : 'Your estimate has been approved.';
      case 'declined':
        return onsite ? 'Your on-site approval estimate has been declined.' : 'Your estimate has been declined.';
      case 'agreed':
        return "You agreed to work with RFI's updated values. RFI has been told and will approve your estimate. You will be updated here as soon as it is approved.";
      case 'customer_approval':
        return 'Please wait for customer confirmation. We are processing your on-site approval.';
      default:
        return null;
    }
  }

  /** "Hours 3 → 2 · Rate $85 → $70" for a line RFI changed, or "Added by RFI" for a line RFI added. */
  protected lineDiffText(line: JobDetailsEstimateLineItem): string | null {
    if (!line.isAdminUpdate) {
      return null;
    }
    if (line.previousTotal == null && line.previousQuantity == null && line.previousRate == null) {
      return 'Added by RFI';
    }
    const parts: string[] = [];
    const unit = line.isLabor ? 'Hours' : 'Qty';
    if (line.previousQuantity != null && Math.abs(line.previousQuantity - (line.quantity ?? 0)) > 0.004) {
      parts.push(`${unit} ${this.trimNumber(line.previousQuantity)} → ${this.trimNumber(line.quantity ?? 0)}`);
    }
    if (line.previousRate != null && Math.abs(line.previousRate - (line.rate ?? 0)) > 0.004) {
      parts.push(`Rate $${line.previousRate.toFixed(2)} → $${(line.rate ?? 0).toFixed(2)}`);
    }
    return parts.length ? parts.join(' · ') : null;
  }

  /** What RFI changed on a line, as separate parts so the template can strike out the older value: "Hours ~~15~~ → 12.5 · Rate ~~$65.69~~ → $65.96". */
  protected lineDiffParts(line: JobDetailsEstimateLineItem): { label: string; prev: string; now: string }[] {
    if (!line.isAdminUpdate) {
      return [];
    }
    const parts: { label: string; prev: string; now: string }[] = [];
    const unit = line.isLabor ? 'Hours' : 'Qty';
    if (line.previousQuantity != null && Math.abs(line.previousQuantity - (line.quantity ?? 0)) > 0.004) {
      parts.push({ label: unit, prev: this.trimNumber(line.previousQuantity), now: this.trimNumber(line.quantity ?? 0) });
    }
    if (line.previousRate != null && Math.abs(line.previousRate - (line.rate ?? 0)) > 0.004) {
      parts.push({ label: 'Rate', prev: `$${line.previousRate.toFixed(2)}`, now: `$${(line.rate ?? 0).toFixed(2)}` });
    }
    return parts;
  }

  protected readonly laborDisplayName = laborDisplayName;

  protected estimateCardTitle(estimate: JobDetailsEstimateSummary): string {
    return estimate.isOnsiteApproval ? 'Your On-site Approval' : 'Your Estimate';
  }

  /** True only for admin-driven changes — a vendor's own edit must never trigger this for
   * themselves. See JobDetailsEstimateLineItemDto.IsAdminUpdate. */
  protected hasChangedLineItems(estimate: JobDetailsEstimateSummary): boolean {
    return estimate.lineItems.some((line) => line.isAdminUpdate);
  }

  protected canApproveEstimate(estimate: JobDetailsEstimateSummary): boolean {
    return estimate.canApproveAsIs;
  }

  protected approveEstimate(estimate: JobDetailsEstimateSummary): void {
    const vendorKey = this.lastVendorKey;
    if (!estimate.estimateKey || !vendorKey || this.approvingEstimateKey()) {
      return;
    }

    const total = estimate.estimateTotal != null ? ` of ${this.formatEstimateCurrency(estimate.estimateTotal)}` : '';
    if (!window.confirm(`Agree to work with RFI's updated estimate${total}? RFI will be told you accept these values and will then approve your estimate. Nothing is approved until RFI does it.`)) {
      return;
    }
    this.approvingEstimateKey.set(estimate.estimateKey);
    this.approveError.set(null);
    this.jobDetailsService
      .approveEstimate(estimate.estimateKey, { vendorKey, counterVersion: estimate.counterVersion ?? null })
      .pipe(
        tap((result) => {
          if (result.outcome === 'success') {
            // The vendor's agreement goes to RFI; nothing is approved. RFI approves from the Admin Portal.
            this.markEstimateAgreedLocally(estimate.estimateKey);
            this.reloadData();
          } else if (result.outcome === 'apiMessage') {
            this.approveError.set(result.message);
            // RFI sent a newer counter meanwhile: show the vendor the latest one before they accept.
            if (result.responseCode === 409) {
              this.reloadData();
            }
          } else {
            this.approveError.set('Could not send your agreement to RFI. Please try again.');
          }
        }),
        finalize(() => this.approvingEstimateKey.set(null)),
        take(1),
      )
      .subscribe();
  }

  private buildDraft(estimate: JobDetailsEstimateSummary): EditLineDraft[] {
    const draft: EditLineDraft[] = [];
    for (const line of estimate.lineItems) {
      if (!line.lineItemKey) {
        continue;
      }
      draft.push({
        draftId: line.lineItemKey,
        lineItemKey: line.lineItemKey,
        isNew: false,
        markedDeleted: false,
        isLabor: line.isLabor,
        itemName: line.itemName ?? '',
        description: line.itemDescription ?? '',
        adminMessage: line.adminMessage?.trim() || '',
        // Labor: the real hours per technician and technician count (quantity is hours x technicians).
        qty: String(line.isLabor ? (line.laborHours ?? line.quantity ?? 0) : (line.quantity ?? 0)),
        rate: String(line.rate ?? 0),
        techOnSite: line.isLabor ? String(line.techs && line.techs > 0 ? line.techs : 1) : '',
        isRateLocked: line.isRateLocked,
        isQuantityLocked: line.isQuantityLocked,
        // Existing lines never show the new-line type picker — it only applies to lines added
        // via addNewLine in this same editing session.
        newItemTypeKey: '',
        isNewTrip: false,
        isNewHelper: false,
        estimateKey: estimate.estimateKey ?? '',
        // A line may always stay at its current value (RFI's counter can leave it above what the vendor submitted, e.g. a
        // locked rate), so the limit is never below what the line is now. The server applies the same rule.
        maxQty: line.vendorMaxQty != null ? Math.max(line.vendorMaxQty, Math.round(Number(line.quantity ?? 0) * 100) / 100) : null,
        maxRate: line.vendorMaxRate != null ? Math.max(line.vendorMaxRate, Math.round(Number(line.rate ?? 0) * 100) / 100) : null,
        previousTotal: line.isAdminUpdate && line.previousTotal != null ? line.previousTotal : null,
      });
    }
    return draft;
  }

  /** Right after "Accept and Agree" succeeds: the vendor has answered RFI (nothing is approved), so every editing control
   * goes away at once (the refresh that follows confirms it from the server). */
  private markEstimateAgreedLocally(estimateKey: string | null): void {
    const data = this.jobData();
    if (!estimateKey || !data) {
      return;
    }
    this.cancelEditEstimate();
    this.jobData.set({
      ...data,
      estimates: (data.estimates ?? []).map((e) =>
        e.estimateKey === estimateKey
          ? { ...e, stateCode: 'agreed', statusLabel: 'Agreed', adminNote: null, canEdit: false, canApproveAsIs: false, vendorCanAddLines: false }
          : e,
      ),
    });
  }

  /** Shown after RFI acted (declined, approved, ...) while the vendor had the edit form open. */
  protected readonly editInterruptedNotice = signal<string | null>(null);

  /** The admin can act while the vendor is mid-edit: when an estimate being edited stops being editable (declined,
   * approved, back with RFI ...), close the form so nothing can be saved against it. */
  private closeEditIfNoLongerAllowed(): void {
    const keys = this.editingEstimateKeys();
    if (keys.length === 0) {
      return;
    }
    const estimates = this.estimates();
    const blocked = keys.some((key) => {
      const estimate = estimates.find((e) => e.estimateKey === key);
      return !estimate || !estimate.canEdit;
    });
    if (blocked) {
      this.cancelEditEstimate();
      this.editInterruptedNotice.set(
        'RFI has responded to this estimate, so it can no longer be edited. Your unsaved changes were discarded.',
      );
    }
  }

  /** Reject for Resubmission (revision_requested): the vendor has nothing to review first, so the edit form is what they see. */
  protected readonly opensStraightIntoEdit = (estimate: JobDetailsEstimateSummary): boolean =>
    estimate.stateCode === 'revision_requested' && estimate.canEdit;

  /**
   * An estimate RFI sent back with Reject for Resubmission opens in the edit form on its own (no read-only view with an Edit Estimate
   * button in between). Never while a form is already open (a live refresh must not wipe what the vendor is typing), while a save is
   * running, or in the seconds after one (a refresh that read the data before the save would otherwise reopen the form).
   */
  private openRevisionEditIfNeeded(): void {
    if (this.editingEstimateKeys().length > 0 || this.editSaving() || this.editSavedEstimateKey() !== null) {
      return;
    }
    const single = this.estimates().find((e) => !e.isMultiOption && this.opensStraightIntoEdit(e));
    if (single) {
      this.startEditEstimate(single);
      return;
    }
    const group = this.multiOptionGroups().find((g) => g.options.some(this.opensStraightIntoEdit));
    if (group) {
      this.startEditGroup(group.groupKey);
    }
  }

  /** The vendor adds at most one trip charge per estimate (normal or overtime): no "+ Add Trip Line" once the form has one. A trip line
   * the vendor removed in this form does not count, so it can be replaced. */
  protected canAddTripLine(estimateKey: string | null): boolean {
    return !this.draftFor(estimateKey).some((line) => !line.markedDeleted && isTripChargeDraft(line));
  }

  protected startEditEstimate(estimate: JobDetailsEstimateSummary): void {
    this.editInterruptedNotice.set(null);
    this.editDraft.set(this.buildDraft(estimate));
    this.editError.set(null);
    this.editShowErrors.set(false);
    this.editingGroupKey.set(null);
    this.editingEstimateKeys.set([estimate.estimateKey ?? '']);
  }

  /** One Edit for the whole multiple-option estimate: every editable option opens at once. */
  protected startEditGroup(groupKey: string): void {
    this.editInterruptedNotice.set(null);
    const group = this.multiOptionGroups().find((g) => g.groupKey === groupKey);
    if (!group || group.editable.length === 0) {
      return;
    }
    this.editDraft.set(group.editable.flatMap((option) => this.buildDraft(option)));
    this.editError.set(null);
    this.editShowErrors.set(false);
    this.editingGroupKey.set(groupKey);
    this.editingEstimateKeys.set(group.editable.map((o) => o.estimateKey ?? ''));
  }

  protected cancelEditEstimate(): void {
    this.editingEstimateKeys.set([]);
    this.editingGroupKey.set(null);
    this.editDraft.set([]);
    this.editError.set(null);
    this.editShowErrors.set(false);
  }

  protected onEditFieldInput(
    draftId: string,
    field: 'qty' | 'rate' | 'techOnSite' | 'itemName' | 'description',
    value: string,
  ): void {
    this.editDraft.update((draft) =>
      draft.map((line) => (line.draftId === draftId ? { ...line, [field]: value } : line)),
    );
    // Once every flagged line is fixed, drop the stale "NOT SAVED" banner.
    if (this.editShowErrors() && Object.keys(this.editLineProblems()).length === 0) {
      this.editError.set(null);
    }
  }

  /** "material" gets no type picker (only one kind exists); "labor"/"trip" default to their
   * first (non-emergency) option and get a dropdown in the template. */
  protected addNewLine(lineKind: 'labor' | 'material' | 'trip' | 'helper', forEstimateKey?: string | null): void {
    const draftId = `new-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    // A multiple-option estimate passes the option the button belongs to: the line is added to that option only.
    const estimateKey = forEstimateKey ?? this.editingEstimateKey();
    if (!estimateKey) {
      return;
    }
    const estimate = this.estimates().find((e) => e.estimateKey === estimateKey) ?? null;
    // While RFI's counter is going back and forth the vendor can't add lines (only after a reject for resubmission).
    if (estimate && estimate.vendorCanAddLines === false) {
      return;
    }
    const isHelper = lineKind === 'helper';
    const isLabor = lineKind === 'labor' || isHelper;
    const isTrip = lineKind === 'trip';
    if (isTrip && !this.canAddTripLine(estimateKey)) {
      return;
    }
    // New labor/trip/helper lines are locked to the vendor's profile rate the same as existing
    // ones — see backend SaveVendorPortalEstimateEdit. Material has no locked-rate concept.
    const lockedRate = isHelper
      ? estimate?.lockedHelperRate ?? null
      : isLabor
        ? estimate?.lockedLaborRate ?? null
        : isTrip
          ? estimate?.lockedTripRate ?? null
          : null;
    // Helper has no picker: the job type decides standard vs emergency (the server picks the
    // matching rate the same way), so the name follows the job.
    const defaultOptions = isHelper
      ? null
      : isLabor
        ? NEW_LABOR_ITEM_TYPE_OPTIONS
        : isTrip
          ? NEW_TRIP_ITEM_TYPE_OPTIONS
          : null;
    this.editDraft.update((draft) => [
      ...draft,
      {
        draftId,
        lineItemKey: '',
        isNew: true,
        markedDeleted: false,
        isLabor,
        itemName: NEW_LINE_SAVED_ITEM_NAMES[
          isHelper
            ? this.isEmergencyJob()
              ? 'EMERGENCY_HELPER'
              : 'HELPER'
            : defaultOptions
              ? defaultOptions[0].key
              : 'MATERIAL'
        ],
        description: '',
        qty: isTrip ? '1' : '0',
        rate: lockedRate != null ? String(lockedRate) : '0',
        techOnSite: isLabor ? '1' : '',
        isRateLocked: lockedRate != null,
        isQuantityLocked: isTrip && lockedRate != null,
        // A helper line starts on the job's own kind (the name above); Labor / Trip start on the first option.
        newItemTypeKey: isHelper
          ? this.isEmergencyJob()
            ? 'EMERGENCY_HELPER'
            : 'HELPER'
          : defaultOptions
            ? defaultOptions[0].key
            : '',
        isNewTrip: isTrip,
        isNewHelper: isHelper,
        estimateKey,
        maxQty: null,
        maxRate: null,
        previousTotal: null,
      },
    ]);
  }

  /** Same test the server uses to pick emergency rates (job type key), with the type name as a
   * fallback so a missing key can't silently label an emergency job's helper "Helper". */
  private isEmergencyJob(): boolean {
    const data = this.jobData();
    return (
      data?.jobTypeKey?.toLowerCase() === EMERGENCY_JOB_TYPE_KEY ||
      /emergency/i.test(data?.jobTypeName ?? '')
    );
  }

  /** Dropdown selection only ever changes the display label — no other draft field or backend
   * behavior depends on which option is picked (see NewLineItemTypeOption doc comment). */
  protected onNewLineTypeChange(draftId: string, optionKey: string): void {
    const target = this.editDraft().find((l) => l.draftId === draftId);
    const options = target?.isNewHelper
      ? NEW_HELPER_ITEM_TYPE_OPTIONS
      : target?.isLabor
        ? NEW_LABOR_ITEM_TYPE_OPTIONS
        : NEW_TRIP_ITEM_TYPE_OPTIONS;
    const option = options.find((o) => o.key === optionKey);
    if (!option) return;
    this.editDraft.update((draft) =>
      draft.map((line) => {
        if (line.draftId !== draftId) {
          return line;
        }
        const changed = { ...line, newItemTypeKey: option.key, itemName: NEW_LINE_SAVED_ITEM_NAMES[option.key] ?? option.label };
        // The rate follows the type picked: Emergency Labor / Emergency Trip bill at the emergency rate, Labor / Trip at the standard one.
        const estimate = this.estimates().find((e) => e.estimateKey === line.estimateKey) ?? null;
        const rate = this.rateForNewLineType(estimate, option.key);
        if (rate === undefined) {
          return changed; // an older API build sends no per-type rates: leave the rate as it was
        }
        return {
          ...changed,
          rate: rate != null ? String(rate) : '0',
          isRateLocked: rate != null,
          ...(line.isNewTrip ? { isQuantityLocked: rate != null } : {}),
        };
      }),
    );
  }

  /** The vendor's locked rate for a new line's picked type: a number, null when none is on file, undefined when the API does not say. */
  private rateForNewLineType(estimate: JobDetailsEstimateSummary | null, optionKey: string): number | null | undefined {
    switch (optionKey) {
      case 'LABOR':
        return estimate?.lockedLaborRateStandard;
      case 'EMERGENCY_LABOR':
        return estimate?.lockedLaborRateEmergency;
      case 'TRIP':
        return estimate?.lockedTripRateStandard;
      case 'EMERGENCY_TRIP':
        return estimate?.lockedTripRateEmergency;
      case 'HELPER':
        return estimate?.lockedHelperRateStandard;
      case 'EMERGENCY_HELPER':
        return estimate?.lockedHelperRateEmergency;
      default:
        return undefined;
    }
  }

  protected removeLine(draftId: string): void {
    this.editDraft.update((draft) => {
      const line = draft.find((l) => l.draftId === draftId);
      if (!line) {
        return draft;
      }
      // A newly-added, never-persisted line just disappears; an existing line is soft-marked
      // for deletion so the vendor can undo before saving.
      if (line.isNew) {
        return draft.filter((l) => l.draftId !== draftId);
      }
      return draft.map((l) => (l.draftId === draftId ? { ...l, markedDeleted: true } : l));
    });
  }

  protected undoRemoveLine(draftId: string): void {
    this.editDraft.update((draft) =>
      draft.map((l) => (l.draftId === draftId ? { ...l, markedDeleted: false } : l)),
    );
  }

  /**
   * Validates one option's draft and turns it into the request lines. Returns null (with the banner set) when
   * the option can't be saved. `optionLabel` prefixes the message when several options are saved together.
   */
  private buildEditLines(draft: EditLineDraft[], optionLabel: string | null): VendorPortalEstimateLineEdit[] | null {
    const prefix = optionLabel ? `${optionLabel}: ` : '';
    const remaining = draft.filter((l) => !l.markedDeleted);
    if (remaining.length === 0) {
      this.editError.set(`${prefix}An estimate must have at least one line item.`);
      return null;
    }

    const blocked = remaining.filter((l) => this.lineProblem(l));
    if (blocked.length > 0) {
      this.editShowErrors.set(true);
      const first = blocked[0];
      const name = first.itemName.trim() || (first.isLabor ? 'Labor' : 'Line item');
      this.editError.set(
        blocked.length === 1
          ? `NOT SAVED — ${prefix}"${name}": ${this.lineProblem(first)} Fix it, or remove the line.`
          : `NOT SAVED — ${prefix}${blocked.length} lines have 0 or invalid values (first: "${name}": ${this.lineProblem(first)}) Fix the highlighted lines, or remove them.`,
      );
      setTimeout(() => document.querySelector('.line-item--invalid')?.scrollIntoView({ block: 'center' }));
      return null;
    }

    const lines: VendorPortalEstimateLineEdit[] = [];
    for (const line of draft) {
      const qty = Number(line.qty);
      const rate = Number(line.rate);
      const techOnSite = line.isLabor ? Number(line.techOnSite) || 1 : null;
      if (Number.isNaN(qty) || Number.isNaN(rate) || qty < 0 || rate < 0) {
        this.editError.set(`${prefix}Please enter valid, non-negative numbers for every line.`);
        return null;
      }
      if (line.isNew && !line.itemName.trim()) {
        this.editError.set(`${prefix}Please name every new line item.`);
        return null;
      }
      // Backstop for the lineProblem gate above — nothing with 0/NaN/Infinity may reach the API.
      if (!line.markedDeleted && !(Number.isFinite(qty) && qty > 0 && Number.isFinite(rate) && rate > 0)) {
        this.editShowErrors.set(true);
        this.editError.set(`NOT SAVED — ${prefix}every line needs a quantity/hours and rate above 0.`);
        return null;
      }

      const changeType = line.markedDeleted
        ? VENDOR_PORTAL_LINE_CHANGE.deleted
        : line.isNew
          ? VENDOR_PORTAL_LINE_CHANGE.new
          : VENDOR_PORTAL_LINE_CHANGE.existing;

      lines.push({
        changeType,
        lineItemKey: line.lineItemKey,
        isLabor: line.isLabor,
        itemName: line.itemName || null,
        description: line.description || null,
        qty,
        rate,
        techOnSite,
        // New lines added during a live negotiation MUST be Proposed (1), never Incurred (0) —
        // the DB proc has no default for this column and an omitted/wrong value silently drops
        // the line out of negotiation. Existing lines keep whatever they already were.
        costIncurred: 1,
      });
    }
    return lines;
  }

  protected submitEditEstimate(estimate: JobDetailsEstimateSummary): void {
    const vendorKey = this.lastVendorKey;
    if (!estimate.estimateKey || !vendorKey || this.editSaving()) {
      return;
    }

    const lines = this.buildEditLines(this.draftFor(estimate.estimateKey), null);
    if (!lines) {
      return;
    }

    this.editSaving.set(true);
    this.editError.set(null);
    this.jobDetailsService
      .editEstimate({ estimateKey: estimate.estimateKey, vendorKey, lines })
      .pipe(
        tap((result) => {
          if (result.outcome === 'success') {
            this.cancelEditEstimate();
            this.showEditSavedConfirmation(estimate.estimateKey);
            this.reloadData();
          } else if (result.outcome === 'apiMessage') {
            this.editError.set(result.message);
          } else {
            this.editError.set('Could not save your changes. Please try again.');
          }
        }),
        finalize(() => this.editSaving.set(false)),
        take(1),
      )
      .subscribe();
  }

  /** Saves every option of the multiple-option estimate in ONE batch; each option's new lines go to that option only. */
  protected submitEditGroup(groupKey: string): void {
    const vendorKey = this.lastVendorKey;
    if (!vendorKey || this.editSaving() || this.editingGroupKey() !== groupKey) {
      return;
    }
    const group = this.multiOptionGroups().find((g) => g.groupKey === groupKey);
    if (!group) {
      return;
    }

    const options: VendorPortalOptionEdit[] = [];
    for (const option of group.editable) {
      const label = option.estimateTitle?.trim() || null;
      const lines = this.buildEditLines(this.draftFor(option.estimateKey ?? ''), label);
      if (!lines) {
        return;
      }
      options.push({ estimateKey: option.estimateKey ?? '', lines });
    }

    this.editSaving.set(true);
    this.editError.set(null);
    this.jobDetailsService
      .editMultiOptionEstimate({ vendorKey, options })
      .pipe(
        tap((result) => {
          if (result.outcome === 'success') {
            this.cancelEditEstimate();
            this.showEditSavedConfirmation(groupKey);
            this.reloadData();
          } else if (result.outcome === 'apiMessage') {
            this.editError.set(result.message);
          } else {
            this.editError.set('Could not save your changes. Please try again.');
          }
        }),
        finalize(() => this.editSaving.set(false)),
        take(1),
      )
      .subscribe();
  }

  private showEditSavedConfirmation(estimateKey: string | null): void {
    if (!estimateKey) {
      return;
    }
    if (this.editSavedTimeoutId != null) {
      clearTimeout(this.editSavedTimeoutId);
    }
    this.editSavedEstimateKey.set(estimateKey);
    this.editSavedTimeoutId = setTimeout(() => {
      this.editSavedEstimateKey.set(null);
      this.editSavedTimeoutId = null;
    }, 5000);
  }

  /** True when the job is fully complete (vendor may close the page). */
  protected readonly isJobFullyComplete = computed((): boolean => {
    const data = this.jobData();
    if (!data) {
      return false;
    }
    if (jobStatusKeyIsComplete(data.jobStatusKey)) {
      return true;
    }
    if (normalizeStatus(data.currentStatus) === 'complete') {
      return true;
    }
    return timelineHasCompletedCompleteStep(data);
  });

  protected readonly descriptionHtml = computed((): SafeHtml | null => {
    const html = this.jobData()?.job.description;
    if (!html) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustHtml(html);
  });

  constructor() {
    this.route.queryParamMap
      .pipe(
        switchMap((q) => {
          const { jobKey, vendorKey } = resolveJobAndVendorKeys(q);
          if (!jobKey || !vendorKey) {
            this.lastJobKey = '';
            this.lastVendorKey = '';
            this.missingParams.set(true);
            this.jobData.set(null);
            this.loadError.set(false);
            this.loadApiMessage.set(null);
            this.loading.set(false);
            return EMPTY;
          }
          this.missingParams.set(false);
          this.lastJobKey = jobKey;
          this.lastVendorKey = vendorKey;
          // Push: the admin acted (counter, reject, approve, ...) -> refetch quietly, no loader.
          this.live.connect(jobKey, vendorKey, () => this.reloadData());
          this.loadChatMessages(jobKey, vendorKey);
          return this.runJobDetailsRequest(jobKey, vendorKey);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.setupTabVisibilityAndCrossTabRefresh();

    effect(() => {
      if (this.negotiateActive() && !this.negotiateChatAutoOpened && !this.chatManuallyClosed) {
        this.negotiateChatAutoOpened = true;
        untracked(() => this.openChatPanel());
      }
    });
  }

  /** Background refresh (focus/visibility/cross-tab/after a save): re-pulls the job details and
   * chat and re-renders every section (actions, timeline, estimates, messages) in place — no
   * loading overlay, no inert page, and the last good data stays on screen if the call fails. */
  protected reloadData(): void {
    const jobKey = this.lastJobKey;
    const vendorKey = this.lastVendorKey;
    if (!jobKey || !vendorKey || this.missingParams()) {
      return;
    }
    if (this.loading()) {
      return;
    }
    if (this.refreshInFlight) {
      // A refresh is already running — it may have read the data before whatever triggered this one
      // (e.g. a save that just committed), so run once more as soon as it finishes instead of dropping this one.
      this.reloadQueued = true;
      return;
    }
    this.refreshInFlight = true;
    this.loadChatMessages(jobKey, vendorKey);
    this.runJobDetailsRequest(jobKey, vendorKey, true)
      .pipe(
        finalize(() => {
          this.refreshInFlight = false;
          if (this.reloadQueued) {
            this.reloadQueued = false;
            this.reloadData();
          }
        }),
        take(1),
      )
      .subscribe();
  }

  private runJobDetailsRequest(
    jobKey: string,
    vendorKey: string,
    silent = false,
  ): Observable<JobDetailsGetResult | null> {
    if (!silent) {
      this.loading.set(true);
      this.loadError.set(false);
      this.loadApiMessage.set(null);
    }
    return this.jobDetailsService.getJobDetails(jobKey, vendorKey).pipe(
      tap((result) => {
        console.log(result);
        if (result.outcome === 'success') {
          this.jobData.set(result.data);
          this.closeEditIfNoLongerAllowed();
          this.openRevisionEditIfNeeded();
          this.loadApiMessage.set(null);
          this.loadError.set(false);
        } else if (silent) {
          // Background refresh failed — keep showing the last good data rather than blanking it.
        } else if (result.outcome === 'apiMessage') {
          this.jobData.set(null);
          this.loadApiMessage.set(result.message);
          this.loadError.set(false);
        } else {
          this.jobData.set(null);
          this.loadApiMessage.set(null);
          this.loadError.set(true);
        }
      }),
      catchError(() => {
        if (!silent) {
          this.jobData.set(null);
          this.loadApiMessage.set(null);
          this.loadError.set(true);
        }
        return of(null);
      }),
      finalize(() => {
        if (!silent) {
          this.loading.set(false);
        }
      }),
    );
  }

  private setupTabVisibilityAndCrossTabRefresh(): void {
    const tabOrWindowActive$ = merge(
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible'),
      ),
      fromEvent(window, 'focus'),
    ).pipe(debounceTime(250));

    tabOrWindowActive$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.reloadData();
    });

    const onStorage = (event: StorageEvent): void => {
      if (event.key !== WORK_ORDER_PORTAL_CROSS_TAB_REFRESH_KEY || !event.newValue) {
        return;
      }
      try {
        const payload = JSON.parse(event.newValue) as WorkOrderPortalCrossTabPayload;
        if (!payload?.jobKey || !payload?.vendorKey || !this.lastJobKey || !this.lastVendorKey) {
          return;
        }
        if (payload.jobKey === this.lastJobKey && payload.vendorKey === this.lastVendorKey) {
          this.reloadData();
        }
      } catch {
        /* ignore */
      }
    };

    window.addEventListener('storage', onStorage);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('storage', onStorage);
    });
  }

  protected setVersion(version: HeaderVersion): void {
    this.headerVersion.set(version);
  }

  /**
   * "Current" step = the last entry (in original, pre-reversed workflow order) that has actually
   * happened — i.e. the boundary between completed and still-pending steps. The API array is a
   * fixed workflow sequence, not always a real chronological log, so completed steps are not
   * guaranteed to land at either end once `timeline()` reverses it for display; we find the real
   * boundary in the original order and map it back to its position in the displayed list.
   */
  protected isCurrentTimelineIndex(index: number): boolean {
    const original = this.jobData()?.timeline ?? [];
    if (!original.length) {
      return false;
    }
    let lastHappenedIndex = -1;
    for (let i = 0; i < original.length; i++) {
      if (original[i].time || original[i].isCompleted) {
        lastHappenedIndex = i;
      }
    }
    if (lastHappenedIndex === -1) {
      return false;
    }
    const displayedIndex = original.length - 1 - lastHappenedIndex;
    return index === displayedIndex;
  }

  /** Status label with embedded legacy API datetimes removed. */
  protected formatTimelineStatus(step: JobTimelineEntry): string {
    const status = step.status?.trim();
    if (!status) {
      return '';
    }
    return stripLegacyLocationDateTimeFromStatus(status);
  }

  /**
   * Full timeline title: label + connector (`for` / `to`) + job-location time when available.
   * e.g. "Accepted and ETA set for Jun 21, 2026 at 9:30 AM"
   */
  protected formatTimelineTitle(step: JobTimelineEntry): string {
    const label = this.formatTimelineStatus(step);
    const locationTime = this.formatTimelineLocationTime(step);
    if (!label) {
      return locationTime;
    }
    if (!locationTime) {
      return label;
    }
    return `${label} ${timelineDateConnector(label)} ${locationTime}`;
  }

  /**
   * Job-location datetime from the API (`status` embedded legacy string or `etaTimeLocalize`),
   * formatted as e.g. "Jun 19, 2026 at 2:11 PM". Does not use `etaTimeUTC` (browser TZ).
   */
  protected formatTimelineLocationTime(step: JobTimelineEntry): string {
    const formatPattern = "MMM d, y 'at' h:mm a";

    if (step.status) {
      const fromStatus = extractLegacyLocationDateTimeFromStatus(step.status);
      if (fromStatus) {
        return formatDate(fromStatus, formatPattern, this.locale);
      }
    }

    const localized = step.etaTimeLocalize?.trim();
    if (localized) {
      const fromLocalize = parseWallClockIso(localized);
      if (fromLocalize) {
        return formatDate(fromLocalize, formatPattern, this.locale);
      }
    }

    return '';
  }

  protected formatStepDate(step: JobTimelineEntry): string {
    const d = parseUtcIsoToDate(step.time);
    if (!d) {
      return '';
    }
    return formatDate(d, 'MMM d', this.locale);
  }

  protected formatStepTime(step: JobTimelineEntry): string {
    const d = parseUtcIsoToDate(step.time);
    if (!d) {
      return '';
    }
    return formatDate(d, 'h:mm a', this.locale);
  }

  protected formatEstimateDate(iso: string | null): string {
    const d = parseUtcIsoToDate(iso);
    if (!d) {
      return '';
    }
    return formatDate(d, 'MMM d, y', this.locale);
  }

  protected formatEstimateCurrency(amount: number | null): string {
    if (amount == null) {
      return '';
    }
    return new Intl.NumberFormat(this.locale, { style: 'currency', currency: 'USD' }).format(amount);
  }

  protected isEmergencyJobType(name: string | null | undefined): boolean {
    return normalizeStatus(name ?? '') === 'emergency';
  }

  /** RFI-363: "download the app" prompt shown instead of opening a check-in / check-out link. */
  protected readonly appPrompt = signal<{
    step: 'check-in' | 'check-out';
    appStoreLink: string | null;
    playStoreLink: string | null;
  } | null>(null);

  protected onActionClick(action: JobDetailsAction, data: JobDetailsData): void {
    if (action.mobileAppStep) {
      const appStoreLink = data.mobileApp?.appStoreLink ?? null;
      const playStoreLink = data.mobileApp?.playStoreLink ?? null;

      const showPrompt = (): void =>
        this.appPrompt.set({ step: action.mobileAppStep!, appStoreLink, playStoreLink });

      // A web page can't tell whether an app is installed, so let the phone decide: try the app's deep link
      // (opens this job's check-in / check-out screen when the app is installed) and fall back to the store.
      const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
      const deepLink = action.mobileAppDeepLink?.trim() ?? '';
      const schemeEnd = deepLink.indexOf('://');

      if (deepLink && schemeEnd > 0) {
        if (/Android/i.test(ua) && playStoreLink) {
          // Android Chrome: the intent opens the app if installed, otherwise goes to the Play Store by itself.
          const scheme = deepLink.slice(0, schemeEnd);
          const rest = deepLink.slice(schemeEnd + 3);
          const pkg = data.mobileApp?.androidPackage ?? 'com.retailfixit.vendor';
          window.location.href =
            `intent://${rest}#Intent;scheme=${scheme};package=${pkg};` +
            `S.browser_fallback_url=${encodeURIComponent(playStoreLink)};end`;
          return;
        }

        if (/iPhone|iPad|iPod/i.test(ua)) {
          // iOS has no intent fallback: open the scheme, and if this page is still visible shortly after,
          // the app didn't take over -> App Store (or the prompt when no App Store link is saved).
          let left = false;
          const markLeft = (): void => {
            left = true;
          };
          document.addEventListener('visibilitychange', markLeft);
          window.addEventListener('pagehide', markLeft);
          window.location.href = deepLink;
          window.setTimeout(() => {
            document.removeEventListener('visibilitychange', markLeft);
            window.removeEventListener('pagehide', markLeft);
            if (left || document.hidden) {
              return;
            }
            if (appStoreLink) {
              window.location.href = appStoreLink;
            } else {
              showPrompt();
            }
          }, 1600);
          return;
        }
      }

      // Desktop, or no usable link for this phone: explain and offer the saved store links.
      showPrompt();
      return;
    }
    this.openActionUrl(action.url);
  }

  protected closeAppPrompt(): void {
    this.appPrompt.set(null);
  }

  /** Only the stores that have a link, the one matching the vendor's phone first. */
  protected appPromptStores(): { label: string; url: string; primary: boolean }[] {
    const p = this.appPrompt();
    if (!p) {
      return [];
    }
    const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
    const isIos = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const stores: { label: string; url: string; primary: boolean }[] = [];
    if (p.appStoreLink) {
      stores.push({ label: 'Download on the App Store', url: p.appStoreLink, primary: isIos });
    }
    if (p.playStoreLink) {
      stores.push({ label: 'Get it on Google Play', url: p.playStoreLink, primary: isAndroid });
    }
    stores.sort((a, b) => Number(b.primary) - Number(a.primary));
    if (stores.length === 1) {
      stores[0].primary = true;
    }
    return stores;
  }

  protected openActionUrl(url: string): void {
    const u = fixMissingColonAfterScheme(url?.trim() ?? '');
    if (!u) {
      return;
    }
    window.open(upgradeHttpToHttpsWhenPageIsSecure(u), '_blank', 'noopener,noreferrer');
  }

  protected actionIsDecline(action: JobDetailsAction): boolean {
    return /decline/i.test(action.text ?? '');
  }

  protected actionIsPrimary(index: number, action: JobDetailsAction): boolean {
    return index === 0 && !this.actionIsDecline(action);
  }

  protected actionIsSecondary(index: number, action: JobDetailsAction): boolean {
    return index > 0 && !this.actionIsDecline(action);
  }

  private pickImageFiles(list: FileList | File[]): File[] {
    return Array.from(list).filter((f) => f.type.startsWith('image/'));
  }

  protected onAttachmentDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.attachmentDragDepth++;
    this.attachmentDragActive.set(true);
  }

  protected onAttachmentDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  protected onAttachmentDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.attachmentDragDepth = Math.max(0, this.attachmentDragDepth - 1);
    if (this.attachmentDragDepth === 0) {
      this.attachmentDragActive.set(false);
    }
  }

  protected onAttachmentDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.attachmentDragDepth = 0;
    this.attachmentDragActive.set(false);
    const dt = event.dataTransfer;
    if (!dt?.files?.length) {
      return;
    }
    this.enqueueAttachmentFiles(this.pickImageFiles(dt.files));
  }

  protected onAttachmentFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) {
      return;
    }
    this.enqueueAttachmentFiles(this.pickImageFiles(files));
    input.value = '';
  }

  protected onAttachmentRemarksInput(event: Event): void {
    this.attachmentRemarks.set((event.target as HTMLTextAreaElement).value);
  }

  private enqueueAttachmentFiles(files: File[]): void {
    if (!files.length) {
      return;
    }
    this.attachmentFeedback.set(null);
    this.attachmentQueue.update((q) => [...q, ...files]);
  }

  protected removeAttachmentAt(index: number): void {
    this.attachmentQueue.update((q) => q.filter((_, i) => i !== index));
  }

  protected formatAttachmentSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** True when the vendor has files (and optional note) ready to send. */
  protected canSendAttachments(jobKey: string | null | undefined): boolean {
    if (this.attachmentUploading() || !jobKey?.trim() || !this.lastVendorKey) {
      return false;
    }
    return this.attachmentQueue().length > 0;
  }

  /** Maps terse API messages (e.g. "Ok") to a clear user-facing success line. */
  private attachmentSuccessUserMessage(apiMessage: string | undefined, fileCount: number): string {
    const trimmed = apiMessage?.trim() ?? '';
    const lower = trimmed.toLowerCase();
    const generic = !trimmed || ['ok', 'success', 'done', 'true'].includes(lower);
    if (generic) {
      return fileCount === 1
        ? 'Your file uploaded successfully.'
        : `Your ${fileCount} files uploaded successfully.`;
    }
    return trimmed;
  }

  protected uploadAttachments(jobKey: string): void {
    const files = this.attachmentQueue();
    const snap = this.route.snapshot.queryParamMap;
    const vendorKey = (
      queryLc(snap, 'vendorkey') ||
      queryLcUrl('vendorkey') ||
      this.lastVendorKey
    ).trim();
    if (!this.canSendAttachments(jobKey) || !vendorKey) {
      return;
    }
    const uploadedFileCount = files.length;
    this.attachmentUploading.set(true);
    this.attachmentFeedback.set(null);

    const fd = new FormData();
    fd.append('request.JobKey', jobKey);
    const remarks = this.attachmentRemarks().trim();
    if (remarks) fd.append('request.Remarks', remarks);
    for (const f of files) {
      fd.append('request.FileAttachment', f, f.name);
    }

    this.jobAttachmentsService
      .saveAttachments(fd, vendorKey)
      .pipe(
        take(1),
        finalize(() => this.attachmentUploading.set(false)),
      )
      .subscribe({
        next: (res) => {
          if (res.status) {
            this.attachmentFeedback.set({
              kind: 'success',
              text: this.attachmentSuccessUserMessage(res.message, uploadedFileCount),
            });
            this.attachmentQueue.set([]);
            this.attachmentRemarks.set('');
            const input = this.attachmentFileInput()?.nativeElement;
            if (input) {
              input.value = '';
            }
          } else {
            this.attachmentFeedback.set({
              kind: 'error',
              text: res.message?.trim() || 'Upload failed.',
            });
          }
        },
        error: (err: unknown) => {
          const httpErr = err as { error?: { message?: string }; message?: string };
          const msg =
            (typeof httpErr?.error?.message === 'string' && httpErr.error.message) ||
            (typeof httpErr?.message === 'string' && httpErr.message) ||
            'Upload failed.';
          this.attachmentFeedback.set({ kind: 'error', text: msg });
        },
      });
  }

  // ---- Messages accordion ----

  private loadChatMessages(jobKey: string, vendorKey: string): void {
    if (!jobKey.trim() || !vendorKey.trim()) {
      return;
    }
    this.chatLoading.set(true);
    this.jobChatService
      .getMessages(jobKey, vendorKey)
      .pipe(
        take(1),
        finalize(() => this.chatLoading.set(false)),
      )
      .subscribe((result) => {
        this.chatLoadedOnce = true;
        if (result.outcome !== 'success') {
          // Chat is a secondary feature on this page — a transient failure here shouldn't
          // disrupt the job details view, so we just leave the prior thread state as-is.
          return;
        }
        this.chatMessages.set(result.data.items);
        if (result.data.unreadCount > 0 && !this.chatManuallyClosed) {
          this.chatOpen.set(true);
          // Auto-opening counts as viewing the thread — mark read server-side and reflect that
          // immediately in the badge, rather than showing a stale count until the next poll.
          this.chatUnreadCount.set(0);
          this.markChatThreadRead();
        } else {
          this.chatUnreadCount.set(result.data.unreadCount);
        }
        this.scrollChatToBottom();
      });
  }

  /** Advances the server-side read cursor — fire-and-forget, doesn't block the UI either way. */
  private markChatThreadRead(): void {
    if (!this.lastJobKey.trim() || !this.lastVendorKey.trim()) {
      return;
    }
    this.jobChatService.markRead(this.lastJobKey, this.lastVendorKey).pipe(take(1)).subscribe();
  }

  /** Jumps the thread to the newest message. Deferred a tick so it runs after Angular has
   * actually painted the `@for` update — scrolling immediately would measure the old height. */
  private scrollChatToBottom(): void {
    setTimeout(() => {
      const el = this.chatThreadEl()?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }

  /** Opens the Messages panel: marks the thread read, loads it if it has not loaded yet, and starts the refresh poll. */
  private openChatPanel(): void {
    this.chatOpen.set(true);
    this.chatManuallyClosed = false;
    this.chatUnreadCount.set(0);
    this.markChatThreadRead();
    if (!this.chatLoadedOnce) {
      this.loadChatMessages(this.lastJobKey, this.lastVendorKey);
    }
    this.startChatPolling();
    this.scrollChatToBottom();
  }

  protected toggleChatAccordion(): void {
    const opening = !this.chatOpen();
    if (opening) {
      this.openChatPanel();
    } else {
      this.chatOpen.set(false);
      this.chatManuallyClosed = true;
      this.stopChatPolling();
    }
  }

  private startChatPolling(): void {
    this.stopChatPolling();
    this.chatPollSub = timer(CHAT_POLL_INTERVAL_MS, CHAT_POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.chatOpen()) {
          this.loadChatMessages(this.lastJobKey, this.lastVendorKey);
        }
      });
  }

  private stopChatPolling(): void {
    this.chatPollSub?.unsubscribe();
    this.chatPollSub = null;
  }

  protected onChatComposerInput(event: Event): void {
    this.chatComposerText.set((event.target as HTMLInputElement).value);
  }

  protected canSendChatMessage(): boolean {
    return (
      !this.chatSending() &&
      this.chatComposerText().trim().length > 0 &&
      !!this.lastJobKey &&
      !!this.lastVendorKey
    );
  }

  protected sendChatMessage(): void {
    if (!this.canSendChatMessage()) {
      return;
    }
    const body = this.chatComposerText().trim();
    this.chatSending.set(true);
    this.chatFeedback.set(null);
    this.jobChatService
      .sendMessage(this.lastJobKey, this.lastVendorKey, body)
      .pipe(
        take(1),
        finalize(() => this.chatSending.set(false)),
      )
      .subscribe((result) => {
        if (result.outcome === 'success') {
          this.chatMessages.update((msgs) => [...msgs, result.data]);
          this.chatComposerText.set('');
          this.chatUnreadCount.set(0);
          this.scrollChatToBottom();
        } else if (result.outcome === 'apiMessage') {
          this.chatFeedback.set({ kind: 'error', text: result.message });
        } else {
          this.chatFeedback.set({ kind: 'error', text: 'Unable to send message. Please try again.' });
        }
      });
  }

  protected formatChatTime(iso: string): string {
    const d = parseUtcIsoToDate(iso);
    if (!d) {
      return '';
    }
    return formatDate(d, "MMM d, h:mm a", this.locale);
  }
}
